import {
  AcademicCapIcon,
  RocketLaunchIcon,
  ArrowUpTrayIcon,
  UsersIcon,
  HomeIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { Linkedin } from 'lucide-react';

function JoinUsHero() {
  return (
    <section className="relative mx-auto overflow-hidden px-6 pb-32 pt-40">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl text-left">
        {/* Caption */}
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-purple-500 dark:text-purple-400">
          Let's Build Something Great Together
        </span>

        <h1 className="mb-8 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="animate-fadeUp dark:gray-100 block">Be a Part of</span>
          <span className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent delay-150">
            Fast Growing Team
          </span>
        </h1>

        <p className="animate-fadeUp max-w-2xl text-base leading-relaxed delay-300 sm:text-lg dark:text-gray-100/90">
          If you're passionate about AI, Telco, and data engineering and want to make a real impact,
          we want to hear from you. Join our team of innovators and help us shape the future with
          cutting-edge technology.
        </p>
      </div>
    </section>
  );
}

function WhatYouGet() {
  const benefits = [
    {
      title: 'Work From Anywhere',
      description:
        'We are remote-first by default. Do your best work from where you think best, without pointless office constraints.',
      icon: HomeIcon,
      accent: 'from-indigo-500 to-purple-500',
    },
    {
      title: 'A Real Learning Curve',
      description:
        'You will work on real systems, real users, and real problems. Growth here comes from responsibility, not tutorials.',
      icon: AcademicCapIcon,
      accent: 'from-emerald-500 to-teal-500',
    },
    {
      title: 'AI-Powered Engineering',
      description:
        'We build with modern AI tooling, automation, and intelligent workflows. You learn how AI fits into real production systems.',
      icon: ComputerDesktopIcon,
      accent: 'from-blue-500 to-cyan-500',
    },
  ];

  return (
    <section className="relative mx-auto overflow-hidden px-6 py-28">
      <div className="inset-0 -z-10 mx-auto max-w-6xl">
        {/* Background */}
        <div className="absolute inset-0 -z-10">
          {/* Radial glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
          {/* Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        <div className="absolute inset-0 -z-10">
          <div className="animate-slowFloat absolute -left-32 -top-32 h-96 w-96 rounded-full bg-purple-400/30 blur-3xl dark:bg-purple-600/30" />
          <div className="animate-slowFloat2 absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-pink-400/20 blur-3xl dark:bg-pink-500/30" />
        </div>

        <header className="mb-14 text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl dark:text-gray-100">
            What You Get
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300">
            We do not promise comfort. We offer clarity, ownership, and serious growth.
          </p>
        </header>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.title}
                className="group relative overflow-hidden rounded-3xl border border-gray-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-700 dark:bg-gray-900"
              >
                {/* Accent glow */}
                <div
                  className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${item.accent} opacity-10 blur-2xl transition-opacity group-hover:opacity-20`}
                />

                <div
                  className={`mb-5 inline-flex rounded-xl bg-gradient-to-br ${item.accent} p-3 text-white`}
                >
                  <Icon className="h-6 w-6" />
                </div>

                <h3 className="mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {item.title}
                </h3>

                <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LearnBuildGrow() {
  const features = [
    {
      title: 'Learn',
      description:
        'Access hands-on workshops, AI tutorials, and expert guidance to sharpen your skills.',
      gradient: 'from-purple-500 via-pink-500 to-indigo-500',
      icon: AcademicCapIcon,
    },
    {
      title: 'Build',
      description:
        'Build real-world projects that solve meaningful problems and showcase your talents to the world.',
      gradient: 'from-green-400 via-teal-400 to-blue-500',
      icon: RocketLaunchIcon,
    },
    {
      title: 'Grow',
      description:
        'Expand your expertise, career opportunities, and personal development in a thriving environment.',
      gradient: 'from-yellow-400 via-orange-400 to-red-400',
      icon: ArrowUpTrayIcon,
    },
    {
      title: 'Network',
      description: 'Connect with industry leaders, innovators, and fellow creators in tech and AI.',
      gradient: 'from-pink-400 via-purple-400 to-indigo-500',
      icon: UsersIcon,
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl py-28">
      {/* Background subtle glow */}
      <div className="absolute inset-0 -z-10">
        {/* Floating gradient glows */}
        <div className="animate-slowFloat absolute -left-32 -top-32 h-96 w-96 rounded-full bg-purple-400/30 blur-3xl dark:bg-purple-600/30" />
        <div className="animate-slowFloat2 absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-pink-400/20 blur-3xl dark:bg-pink-500/30" />
      </div>

      <div className="mx-auto max-w-7xl px-6 text-center">
        <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-white">
          Learn, Build, Grow and Network
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-700 sm:text-xl dark:text-gray-300">
          Join a fast growing team where you can work on amazing products, learn from talented
          colleagues, and grow your career in Engineering, AI and Data. Also, be an expert in
          different industries such as Telco, Finance, Retail, and more across the globe.
        </p>
        {/* Feature cards */}
        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className={`group relative overflow-hidden rounded-3xl bg-gradient-to-tr ${f.gradient} transform p-[2px] shadow-lg transition-transform hover:scale-105`}
              >
                <div className="flex h-full flex-col items-center rounded-3xl bg-gray-900/90 p-8 text-center backdrop-blur-xl">
                  <Icon className="mb-4 h-16 w-16 text-white" />
                  <h3 className="text-xl font-bold text-white sm:text-2xl">{f.title}</h3>
                  <p className="mt-2 text-sm text-gray-200 sm:text-base">{f.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CareersLinkedIn() {
  const linkedinJobsUrl = 'https://www.linkedin.com/company/30260322/jobs/';

  return (
    <section className="mx-auto max-w-5xl px-6 py-24">
      <header className="mb-14 text-center">
        <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl dark:text-gray-100">
          <span className="animate-gradientText bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            Available Positions
          </span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-gray-600 dark:text-gray-300">
          We hire thoughtfully. All open roles are published on LinkedIn.
        </p>
      </header>

      {/* Empty / Default State */}
      <div className="rounded-3xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30">
          <Linkedin />
        </div>

        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          No open roles right now
        </h2>

        <p className="mx-auto mt-3 max-w-md text-gray-600 dark:text-gray-300">
          We do not hire for the sake of hiring. When a role opens, it will be posted on LinkedIn
          first.
        </p>

        <a
          href={linkedinJobsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#0A66C2] px-8 py-4 text-sm font-semibold text-white transition hover:opacity-90"
        >
          Apply via LinkedIn
          <Linkedin />
        </a>
      </div>
    </section>
  );
}

export default function Careers() {
  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <JoinUsHero />
      <LearnBuildGrow />
      <WhatYouGet />
      <CareersLinkedIn />
    </div>
  );
}
